import { Injectable } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { Platform } from '@ionic/angular';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  location: GeolocationPosition = null;
  distance: number = null;
  isInHotel = false;

  // Set a timeout for geolocation requests - longer for Safari
  private readonly POSITION_OPTIONS = {
    enableHighAccuracy: true,
    timeout: 30000, // 30 seconds timeout to accommodate Safari
    maximumAge: 0,
  };

  // For Safari/iOS, use less accuracy but faster response
  private readonly SAFARI_POSITION_OPTIONS = {
    enableHighAccuracy: false,
    timeout: 60000, // Even longer timeout for Safari
    maximumAge: 30000, // Allow cached positions for Safari
  };

  constructor(private dataServ: DataService, private platform: Platform) {}

  // Check if running on Safari
  private isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
           this.platform.is('ios') || 
           (this.platform.is('desktop') && /Mac/.test(navigator.platform));
  }

  async permission(): Promise<boolean> {
    try {
      // Special handling for Safari
      if (this.isSafari() && !this.platform.is('capacitor')) {
        console.log('Safari detected, using alternate permission flow');
        try {
          // On Safari, immediately request permissions as checkPermissions may be inconsistent
          const result = await Geolocation.requestPermissions();
          return result.location === 'granted';
        } catch (error) {
          console.error('Safari permission request error:', error);
          return false;
        }
      }

      // Standard flow for other browsers
      const ALLOWED = await Geolocation.checkPermissions();

      if (ALLOWED.location !== 'granted') {
        try {
          const result = await Geolocation.requestPermissions();
          return result.location === 'granted';
        } catch (error) {
          console.error('Error requesting location permissions:', error);
          return false;
        }
      }

      // Permission was already granted
      return true;
    } catch (error) {
      console.error('Error checking location permissions:', error);
      return false;
    }
  }

  async getLocation(): Promise<boolean> {
    try {
      // Use Safari-specific options on Safari browsers
      const options = this.isSafari() && !this.platform.is('capacitor') 
                      ? this.SAFARI_POSITION_OPTIONS 
                      : this.POSITION_OPTIONS;
      
      console.log('Getting location with options:', options);
      
      // Get current position with appropriate timeout options
      this.location = await Geolocation.getCurrentPosition(options);
      
      // Safety check for null coordinates (can happen in some browser implementations)
      if (!this.location?.coords?.latitude || !this.location?.coords?.longitude) {
        console.error('Invalid location data received');
        this.distance = null;
        this.isInHotel = false;
        return false;
      }
      
      // Handle case where hotel data might not be loaded yet
      if (!this.dataServ.rData?.lat_lng) {
        console.error('Hotel location data not available');
        this.distance = null;
        this.isInHotel = false;
        return false;
      }
      
      // Calculate distance between user and hotel
      this.distance = this.calculateDistance(
        this.location.coords.latitude,
        this.location.coords.longitude,
        +this.dataServ.rData.lat_lng.split(',')[0],
        +this.dataServ.rData.lat_lng.split(',')[1]
      );
      
      console.log('Distance calculated:', this.distance, 'km');
      console.log('Hotel radius:', this.dataServ.rData.radius_range / 1000, 'km');
      
      // Check if user is within hotel radius
      this.isInHotel = this.distance <= this.dataServ.rData.radius_range / 1000;
      
      return this.isInHotel;
    } catch (error) {
      console.error('Error getting location:', error);
      // Reset values on error to avoid showing incorrect information
      this.distance = null;
      this.isInHotel = false;
      throw error; // Rethrow to let calling component handle the error
    }
  }

  /**
   * Calculate distance between two geographic points using the Haversine formula
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLon / 2) *
        Math.sin(dLon / 2) *
        Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2));

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  }

  /**
   * Convert degrees to radians
   */
  private toRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  /**
   * Clear location data - useful when resetting state
   */
  clearLocationData(): void {
    this.location = null;
    this.distance = null;
    this.isInHotel = false;
  }
}
